import type { PayoutBatchesTranslations } from "../interfaces/payoutBatches";

export const payoutBatchesTranslations: PayoutBatchesTranslations = {
  // Page
  page_title: "Batch payouts",
  page_subtitle: "Upload a sheet with many payments, review it and run it as one operation.",
  action_new_batch: "New batch",
  action_refresh: "Refresh",
  action_back_to_list: "Back to batches",
  action_view_file: "View original file",
  action_download_report: "Download report",
  action_history: "History",
  action_cancel_batch: "Cancel batch",
  action_revalidate: "Revalidate",
  action_retry_failed: "Retry {{count}} failed",
  action_retry_failed_one: "Retry 1 failed",
  action_retry_failed_other: "Retry {{count}} failed",
  action_resume: "Resume",
  action_upload_another: "Upload another file",
  action_validate_again: "Validate again",
  action_close: "Close",
  action_cancel: "Cancel",
  error_load: "We could not load the batch.",
  error_retry: "Retry",
  error_generic: "Something went wrong. Try again.",
  error_network: "You are offline. Check your connection and try again.",
  not_found_title: "Batch not found",
  not_found_description:
    "The link points to a batch that does not exist or does not belong to your organization.",

  // List
  org_picker_label: "Organization",
  org_picker_placeholder: "Search by name, email or id",
  org_picker_loading: "Loading organizations…",
  org_picker_empty: "No results",
  org_picker_searching: "Searching…",
  org_picker_search_error: "The search failed. Try again.",
  org_picker_catalog_error: "We could not load the organizations.",
  org_picker_catalog_truncated: "The catalog is capped: search by name to reach every organization.",
  org_picker_global_option: "All organizations",
  org_picker_global_hint: "Showing batches of every organization.",
  org_picker_org_hint: "Showing only this organization's batches.",
  org_picker_required_hint: "Pick the organization the batch belongs to.",
  list_filter_corridor_label: "Corridor",
  list_filter_corridor_all: "All corridors",
  list_filter_status_label: "Status",
  list_filter_status_all: "All statuses",
  kpi_all: "All",
  kpi_pending_review: "In review",
  kpi_pending_approval: "To approve",
  kpi_executing: "Running",
  kpi_completed: "Completed",
  kpi_with_errors: "With errors",
  kpi_closed: "Closed",
  list_col_created: "Date",
  list_col_organization: "Organization",
  list_col_corridor: "Corridor",
  list_col_rows: "Payments",
  list_col_debit: "Debit",
  list_col_status: "Status",
  list_col_file: "File",
  list_rows_progress: "{{selected}} of {{total}}",
  list_expires_in: "Expires in {{time}}",
  list_empty_title: "Upload your first batch",
  list_empty_description: "Download the corridor's template, fill in one payment per row and upload it here.",
  list_empty_filtered_title: "No batch matches",
  list_empty_filtered_description: "Change the filters to see other batches.",
  list_live_updated_at: "Updated {{time}}",

  // Setup (create)
  setup_title: "New payout batch",
  setup_subtitle:
    "Choose where the money comes from and which network it goes to, then upload the filled template.",
  step_configure: "Set up",
  step_review: "Review",
  step_approval: "Approval",
  step_confirm: "Payment",
  setup_organization_label: "Organization",
  setup_org_not_enabled_title: "This organization doesn't see batch payouts",
  setup_org_not_enabled_text:
    "Its members don't have the module enabled, so another Coloca team user will have to approve the batch. You can enable it in Organizations.",
  setup_route_title: "Choose origin and destination",
  setup_route_from: "From",
  setup_route_to: "To",
  setup_wallet_placeholder: "Choose the wallet to debit",
  setup_wallet_loading: "Loading wallets…",
  setup_wallet_empty: "This organization has no active wallets.",
  setup_wallet_frozen: "Frozen",
  setup_wallet_available: "Available {{amount}}",
  setup_wallet_required: "Choose the wallet the money comes from.",
  setup_corridor_placeholder: "Choose country and network",
  setup_corridor_description:
    "A batch pays through one network. The template changes with the network you pick.",
  setup_corridor_unavailable: "Unavailable",
  setup_corridor_required: "Choose the country and network the payments go to.",
  setup_template_title: "Download and fill in the template",
  setup_file_title: "Upload the filled file",
  setup_step_done: "Done",
  setup_concept_label: "Concept",
  setup_concept_description: "Optional. Used for the rows that leave their concept empty.",
  setup_concept_placeholder: "September payroll",
  setup_cross_currency_title: "Conversion included",
  setup_cross_currency_text:
    "Your {{source}} wallet converts to {{currency}} inside each payment. Validation shows an indicative rate; the final rate is set when each payment runs, within the configured tolerance.",
  setup_submit: "Upload and validate",
  setup_submit_hint: "We'll ask for your verification code.",
  setup_submitting: "Uploading…",
  setup_two_factor_required: "Enable two-factor authentication to upload payout batches.",
  setup_upload_error_title: "We could not accept the file",
  setup_upload_archive_failed: "We could not store the file. Try again in a moment.",
  template_title: "{{rail}} template",
  template_description:
    "One payment per row. The amount is what the beneficiary receives; fees are added on top.",
  template_download: "Download template",
  template_downloading: "Downloading…",
  template_need_corridor: "Choose origin and destination to download its template.",
  template_error: "We could not download the template.",
  dropzone_instruction: "Click to upload the batch or drag it here",
  dropzone_instruction_drop: "Drop the file here",
  dropzone_helper: "Use the corridor's template: one payment per row.",
  dropzone_constraints: "Max {{max}} MB · .xlsx or .csv",
  dropzone_change: "Change",
  dropzone_remove: "Remove",
  dropzone_file_size: "{{size}} KB",
  file_error_too_large: "The file is {{size}} MB; the maximum is {{max}} MB.",
  file_error_unsupported_type: "Only .xlsx or .csv files are accepted.",
  file_error_required: "Upload the batch file.",

  // How it works
  guide_open: "How it works",
  guide_title: "How batch payouts work",
  guide_description: "From the file to the last payment, in six steps.",
  guide_progress: "Step {{current}} of {{total}}",
  guide_go_to_step: "Go to step {{step}}: {{title}}",
  guide_previous: "Back",
  guide_next: "Next",
  guide_done: "Got it",
  guide_route_title: "Choose origin and destination",
  guide_route_body:
    "Pick the wallet the money comes from and the country and network it goes to. A batch pays through a single network; if the wallet is in another currency, the conversion is included in each payment.",
  guide_template_title: "Fill in the template",
  guide_template_body:
    "Download that network's template and write one payment per row. The amount is what each person receives; fees are added on top.",
  guide_upload_title: "Upload the file",
  guide_upload_body:
    "Upload it with your verification code. We check every row in the background and show you which ones are ready and which have errors or warnings.",
  guide_review_title: "Review the batch",
  guide_review_body:
    "You see how much will be debited, the fees and your balance. Rows with errors are not paid and you can remove the ones you don't want; then you send it for approval.",
  guide_review_body_self:
    "You see how much will be debited, the fees and your balance. Rows with errors are not paid and you can remove the ones you don't want; then you approve it yourself.",
  guide_approval_title: "Someone else approves it",
  guide_approval_body:
    "Another user with approval permission, not whoever uploaded or submitted it, reviews it and pays it with their code. They can also send it back with a reason. If nobody approves it in time, the batch expires.",
  guide_self_approval_title: "You approve it",
  guide_self_approval_body_org_setting:
    "Your organization turned on self-approval: you approve and pay it with your code. If you'd rather someone else review it, send it for approval. Its history marks it as approved without a second approver.",
  guide_self_approval_body_sole_approver:
    "You are the only person in your organization who can approve batches, so you approve and pay it with your code, without sending it to anyone. Its history marks it as approved without a second approver.",
  guide_approval_settings_link: "How approval works in your organization",
  guide_run_title: "Follow the payments",
  guide_run_body:
    "Payments go out one by one and you see the progress live. At the end you download the report and can retry the ones that failed.",
  guide_scene_file: "payroll.xlsx",
  guide_scene_approver: "Approver",
  guide_scene_self_approver: "You, with your code",

  // Statuses
  status_batch_uploaded: "Uploaded",
  status_batch_validating: "Validating",
  status_batch_validation_failed: "Validation failed",
  status_batch_ready: "In review",
  status_batch_pending_approval: "Awaiting approval",
  status_batch_approved: "Approved",
  status_batch_executing: "Running",
  status_batch_completed: "Completed",
  status_batch_completed_with_errors: "Completed with errors",
  status_batch_failed: "Failed",
  status_batch_cancelled: "Cancelled",
  status_batch_expired: "Expired",
  status_item_pending: "Pending",
  status_item_invalid: "Has errors",
  status_item_excluded: "Excluded",
  status_item_selected: "Selected",
  status_item_queued: "Queued",
  status_item_processing: "Processing",
  status_item_submitted: "Submitted",
  status_item_paid: "Paid",
  status_item_failed: "Failed",
  status_item_reversed: "Reversed",
  status_item_cancelled: "Cancelled",

  // Detail
  detail_created_at: "Created",
  detail_created_by: "Created by",
  detail_corridor: "Corridor",
  detail_file: "File",
  detail_rows: "{{count}} rows",
  detail_rows_one: "1 row",
  detail_rows_other: "{{count}} rows",
  detail_updated_at: "Updated {{time}}",
  detail_live: "live",
  detail_polling: "every {{seconds}} s",
  validating_title: "Validating {{count}} rows…",
  validating_description:
    "We check every destination, amount and limit, look for duplicates and price the batch. This screen updates on its own.",
  validation_failed_title: "We could not validate the batch",
  validation_failed_description: "Fix the file and upload it again, or cancel this batch.",
  validation_failed_description_revalidate:
    "Fix the file and upload it again, or cancel this batch. If the cause was fixed outside the file (for example, the organization's verification), validate it again.",
  expiry_banner: "This batch expires in {{time}}. After that it has to be uploaded again.",
  expiry_expired: "The batch expired before it was confirmed.",
  warning_same_file_link: "View the earlier batch",
  warning_fees_updated_title: "Fees changed since the last validation",
  warning_fees_updated_text:
    "We repriced the batch with the current fees. Check the new debit before confirming.",
  warning_fees_updated_fees: "Fees",
  warning_fees_updated_debit: "Total debit",
  warning_fees_updated_before: "Before",
  warning_fees_updated_after: "Now",
  cancel_title: "Cancel the batch",
  cancel_description: "No row will be paid. You can upload the file again whenever you want.",
  cancel_reason_label: "Reason",
  cancel_reason_placeholder: "Optional: why it is cancelled",
  cancel_confirm: "Cancel batch",
  cancel_success: "Batch cancelled.",
  events_title: "Batch history",
  events_description: "Every action and every status change, with who did it.",
  events_empty: "No events yet.",
  events_error: "We could not load the history.",
  events_load_more: "Load more",
  event_meta_code: "Code",
  event_meta_run: "Run {{run}}",
  event_meta_rows: "{{count}} rows",
  event_meta_reason: "Reason",
  event_meta_self_approved: "No second approver",
  event_meta_self_approval_org_setting: "self-approval on",
  event_meta_self_approval_sole_approver: "the only person who could approve",
  event_reason_fees_changed: "fees changed",
  event_type_batch_uploaded: "Batch uploaded",
  event_type_file_archived: "File archived",
  event_type_validation_started: "Validation started",
  event_type_validation_finished: "Validation finished",
  event_type_validation_failed: "Validation failed",
  event_type_item_excluded: "Row excluded",
  event_type_item_included: "Row included",
  event_type_selection_changed: "Selection changed",
  event_type_revalidation_requested: "Revalidation requested",
  event_type_submitted_for_approval: "Sent for approval",
  event_type_approval_rejected: "Sent back by the approver",
  event_type_submission_withdrawn: "Taken back from approval",
  event_type_approved: "Batch approved",
  event_type_provider_balance_acknowledged: "Corridor liquidity acknowledged",
  event_type_execution_enqueued: "Run queued",
  event_type_execution_enqueue_failed: "Run could not be queued",
  event_type_execution_started: "Run started",
  event_type_item_processing: "Row processing",
  event_type_item_submitted: "Row submitted",
  event_type_item_paid: "Row paid",
  event_type_item_failed: "Row failed",
  event_type_item_reversed: "Row reversed",
  event_type_item_status_synced: "Row status updated by the network",
  event_type_batch_halted: "Batch halted",
  event_type_execution_finished: "Run finished",
  event_type_execution_failed: "Run failed",
  event_type_retry_requested: "Retry requested",
  event_type_run_resumed: "Run resumed",
  event_type_cancelled: "Batch cancelled",
  event_type_expired: "Batch expired",
  event_type_stall_resumed: "Run picked up after a stall",
  event_type_alert_sent: "Alert sent",
  actor_system: "System",
  actor_provider: "Payment network",
  actor_coloca_team: "Coloca team",
  actor_you: "You",
  actor_member: "User",

  // Review
  review_title: "Review the batch",
  review_subtitle: "Confirm which rows get paid and how much is debited before you continue.",
  balance_title: "Wallet balance",
  balance_available: "Available",
  balance_debit: "Batch debit",
  balance_remaining: "Left after",
  balance_sufficient: "Enough to pay the selected rows",
  balance_insufficient: "Not enough for the selected rows",
  balance_unknown: "We could not read the balance",
  balance_frozen: "The wallet is frozen: payments cannot run",
  liquidity_title: "Corridor liquidity {{corridor}}",
  liquidity_ok: "Sufficient",
  liquidity_insufficient: "Insufficient",
  liquidity_stale: "Out of date",
  liquidity_unavailable: "Unavailable",
  liquidity_unsupported: "No reading for this network",
  liquidity_available: "Available {{amount}}",
  totals_title: "This batch",
  totals_payments: "{{count}} payments",
  totals_payments_one: "1 payment",
  totals_payments_other: "{{count}} payments",
  totals_receive: "Beneficiaries receive",
  totals_fees: "Fees",
  totals_debit: "Indicative debit",
  totals_rate: "Indicative rate",
  totals_priced_at: "Priced {{time}}",
  totals_fx_notice: "The final rate is set when each payment runs, within the configured tolerance.",
  suggestions_title: "The balance is not enough",
  suggestions_description: "Pick a way to fit the selection to the available balance, or top up the wallet.",
  suggestion_keep_file_order: "Keep the file order",
  suggestion_smallest_first: "Pay the most rows",
  suggestion_largest_first: "Pay the largest amount",
  suggestion_meta: "{{count}} payments · debit {{debit}} · {{remaining}} left",
  suggestion_apply: "Apply",
  items_filter_all: "All",
  items_filter_selected: "Selected",
  items_filter_errors: "With errors",
  items_filter_warnings: "With warnings",
  items_filter_excluded: "Excluded",
  items_filter_processing: "Processing",
  items_filter_submitted: "Submitted",
  items_filter_paid: "Paid",
  items_filter_failed: "Failed",
  items_filter_reversed: "Reversed",
  items_select_all_valid: "Select valid rows",
  items_deselect_all: "Clear selection",
  items_exclude_with_warnings: "Exclude rows with warnings",
  items_select_page: "Select the rows on this page",
  items_select_row: "Select row {{row}}",
  items_col_row: "#",
  items_col_beneficiary: "Beneficiary",
  items_col_receive: "Receives",
  items_col_fee: "Fee",
  items_col_debit: "Debit",
  items_col_rate: "Rate",
  items_col_status: "Status",
  items_col_issues: "Issues",
  items_col_reason: "Reason",
  items_col_payout: "Payment",
  items_empty_title: "No row matches",
  items_empty_description: "Change the filter to see other rows of the batch.",
  items_syncing: "Saving the selection…",
  items_errors_count: "{{count}} errors",
  items_errors_count_one: "1 error",
  items_errors_count_other: "{{count}} errors",
  items_warnings_count: "{{count}} warnings",
  items_warnings_count_one: "1 warning",
  items_warnings_count_other: "{{count}} warnings",
  row_drawer_title: "Row {{row}}",
  row_errors_title: "Errors",
  row_warnings_title: "Warnings",
  row_pricing_title: "Payment cost",
  row_raw_title: "File data",
  row_destination_title: "Destination",
  row_payout_link: "View the payment",
  row_batch_link: "View the other batch",
  row_no_pricing: "No cost computed",
  row_attempt: "Attempt {{attempt}}",
  row_beneficiary_unknown: "No name",
  destination_label_pix: "PIX key ({{type}})",
  destination_label_spei: "CLABE",
  destination_label_cbu_cvu: "{{type}}",
  destination_label_breb: "Bre-B key ({{type}})",
  destination_label_transfiya: "{{type}} account",
  destination_label_pse: "{{type}} account",
  destination_label_usdt: "{{type}} address",
  destination_label_unknown: "Destination",
  destination_label_breb_phone: "Bre-B key · mobile",
  destination_label_breb_email: "Bre-B key · email",
  destination_label_breb_alphanum: "Bre-B key · alphanumeric",
  destination_label_breb_nric: "Bre-B key · document",
  destination_label_breb_merchant: "Bre-B key · merchant",
  row_beneficiary_from_directory: "Name the Bre-B directory reports",
  row_directory_title: "Bre-B directory",
  row_directory_found: "The key exists in the directory and can receive.",
  row_directory_holder: "Holder",
  row_directory_holder_missing: "The directory gives no name",
  row_directory_bank: "Bank",
  row_directory_checked_at: "Checked",
  footer_summary: "{{count}} payments · debit {{debit}}",
  footer_submit: "Send for approval",
  footer_submit_hint: "Another user with approval permission will review and pay it.",
  footer_approve_hint: "You approve it with your code: it needs no second approval.",
  footer_approve_or_submit_hint: "You can approve it with your code, or send it to someone else to approve.",
  footer_blocked_no_write: "Read-only: you don't have permission to prepare batches.",
  approval_footer_approver_hint: "Check the rows and the debit. Approving pays exactly this selection.",
  approval_footer_maker_hint: "Waiting for another user with approval permission to approve it.",
  approval_footer_self_approver_hint: "You can approve it with your code, or take it back to edit it.",
  approval_footer_reader_hint: "Waiting for approval.",
  action_approve: "Approve and pay",
  action_reject: "Send back",
  action_withdraw: "Take back to edit",
  submit_title: "Send for approval",
  submit_description:
    "Someone else with approval permission will review this batch and pay it with their verification code.",
  submit_rows_label: "Payments",
  submit_debit_label: "Estimated debit",
  submit_frozen_note:
    "While it waits, the selection is frozen. To change it, take it back from approval. The batch still expires at the same time.",
  submit_confirm: "Send for approval",
  submit_success: "Batch sent for approval",
  approval_banner_title: "Waiting for approval",
  approval_banner_text_self:
    "You sent it on {{when}}. The selection is frozen until another user approves it or sends it back.",
  approval_banner_text_self_approver:
    "You sent it on {{when}}. The selection is frozen until it is approved or sent back, and you can approve it yourself.",
  approval_banner_settings_link: "How approval works in your organization",
  approval_banner_text_other:
    "Sent for approval on {{when}}. The selection is frozen until it is approved or sent back.",
  returned_banner_title: "The approver sent this batch back ({{when}})",
  returned_banner_text: "Adjust the selection if needed and send it for approval again.",
  reject_title: "Send the batch back",
  reject_description:
    "Nothing is paid. The batch goes back to review with the same selection, and whoever sent it will see your reason.",
  reject_reason_label: "Reason",
  reject_reason_placeholder: "E.g. row 12 pays the wrong account",
  reject_reason_hint: "At least {{count}} characters.",
  reject_confirm: "Send back",
  reject_success: "Batch sent back to review",
  withdraw_title: "Take it back from approval?",
  withdraw_description:
    "The batch goes back to review with the same selection so you can edit it. You'll have to send it again.",
  withdraw_confirm: "Take back",
  withdraw_success: "Batch taken back from approval",
  footer_blocked_insufficient: "The balance is not enough for the selected rows.",
  footer_blocked_syncing: "Saving the selection…",
  footer_blocked_expired: "The batch expired.",
  footer_blocked_none: "Select at least one row.",
  footer_blocked_blockers: "The batch has blockers that prevent running it.",

  // Execute
  execute_title: "Confirm the batch",
  execute_description: "This is what happens when you confirm.",
  execute_payments: "{{count}} payments",
  execute_debit: "Debited from your wallet",
  execute_fees: "Fees included",
  execute_receive: "Beneficiaries receive",
  execute_fx_notice:
    "The rate is indicative: each payment converts when it runs. If the rate moves more than {{pct}}%, that row is not paid and is flagged.",
  execute_fee_notice:
    "Fees are recalculated when each payment runs. If a row's debit rises more than {{pct}}% over what you reviewed, that row is not paid and is flagged.",
  execute_irreversible: "Once confirmed, the payments go out one by one and cannot be stopped from here.",
  execute_self_approval_org_setting:
    "Your organization lets people approve their own batches: this one is paid without a second approval and its history says so.",
  execute_self_approval_sole_approver:
    "You are the only person in your organization who can approve batches: this one is paid without a second approval and its history says so.",
  execute_ack_liquidity_label: "I understand the corridor liquidity risk",
  execute_ack_liquidity_description:
    "We could not confirm the network has enough funds; some payments may wait.",
  execute_reviewed_label: "I reviewed the rows and the amounts",
  execute_confirm: "Confirm and run",
  execute_arming: "Read the summary…",
  execute_sending: "Confirming…",
  execute_conflict_title: "The batch changed since you reviewed it",
  execute_conflict_description:
    "Someone changed the selection or the batch is no longer ready. Review the updated data before confirming.",
  execute_conflict_back: "Review again",
  execute_self_approval_revoked_title: "You can no longer approve it yourself",
  execute_self_approval_revoked_description:
    "Who can approve batches in your organization changed: another person with approval permission must approve it now.",
  execute_unconfirmed_title: "We could not confirm the result",
  execute_unconfirmed_description:
    "The connection dropped after the confirmation was sent. We are re-reading the batch state.",
  execute_done_title: "Batch running",
  execute_done_description: "The payments started going out. You can follow the progress on this screen.",
  execute_busy_description: "Another batch of this organization is already running on this wallet.",
  execute_two_factor_required: "Enable two-factor authentication to run payout batches.",
  execute_success_toast: "Batch confirmed. The payments are going out.",
  error_fingerprint_mismatch: "The batch changed since you reviewed it. Review it again before confirming.",
  approval_uploader_hint:
    "Whoever uploaded the batch or sent it for approval cannot approve its payouts. Another user with approval permission must do it.",
  error_self_approval_forbidden:
    "Whoever uploaded the batch or sent it for approval cannot send it back, and can only approve it if your organization allows it. Another user with approval permission must do it.",
  error_not_ready: "The batch is not ready for this action.",
  error_nothing_selected: "No rows are selected.",
  error_insufficient_funds: "The wallet balance does not cover the selected rows.",
  error_organization_busy: "Another batch of this organization is already running on this wallet.",
  error_provider_balance_ack_required:
    "The corridor liquidity could not be confirmed; acknowledge it to continue.",
  error_expired: "The batch expired before it was confirmed. Upload it again.",
  error_retry_debit_exceeded:
    "The retried rows now cost more than the amount you approved. Review the new prices.",
  error_run_pending:
    "This batch still has payouts from an earlier run waiting to be processed. Resume it before retrying the failed rows.",
  error_retry_rows_changed:
    "The rows changed since you priced the retry. Review the new prices before approving it.",
  error_items_not_retryable: "Only failed rows without a payment in flight can be retried.",
  error_invalid_status: "This action is not allowed in the batch's current status.",
  error_wallet_frozen: "The source wallet is frozen.",
  error_rail_disabled: "This network is under maintenance for now.",
  error_verification_required: "Your organization must complete its verification before sending payments.",
  error_fees_changed:
    "Fees changed while you were reviewing the batch. Review it again to see the updated prices.",
  error_not_pending_approval: "The batch is no longer waiting for approval. Reload to see its status.",
  error_not_enabled: "Batch payouts aren't enabled for your organization.",

  // Progress
  progress_title: "Batch progress",
  progress_legend_paid: "Paid",
  progress_legend_failed: "Failed",
  progress_legend_submitted: "Submitted, awaiting settlement",
  progress_legend_processing: "Processing",
  progress_legend_pending: "Pending",
  progress_processed: "{{done}} of {{total}} processed",
  counters_pending: "Pending",
  counters_processing: "Processing",
  counters_submitted: "Submitted",
  counters_paid: "Paid",
  counters_failed: "Failed",
  counters_reversed: "Reversed",
  result_completed_title: "Batch completed",
  result_completed_description: "Every selected row was paid.",
  result_completed_with_errors_title: "Batch completed with errors",
  result_completed_with_errors_description:
    "Some rows were not paid. Check the reason on each one and retry the ones that apply.",
  result_failed_title: "The run failed",
  result_failed_description:
    "A system error interrupted the batch. Rows already paid are not repeated; you can resume the pending ones.",
  result_cancelled_title: "Batch cancelled",
  result_cancelled_description: "No row was paid.",
  result_expired_title: "Batch expired",
  result_expired_description: "The batch was not confirmed in time. Upload it again if it still applies.",
  result_halted: "The batch stopped after repeated failures: {{reason}}.",
  result_executing_title: "Payments in progress",
  result_executing_description: "Rows are paid one by one. You can leave this screen and come back later.",
  retry_title: "Retry failed rows",
  retry_description: "Review the updated cost of each row before approving the retry.",
  retry_preview_loading: "Computing new prices…",
  retry_preview_error: "We could not compute the new prices.",
  retry_col_row: "Row",
  retry_col_new_debit: "New debit",
  retry_col_issue: "Issue",
  retry_total: "Total debit {{amount}}",
  retry_retryable_count: "{{count}} rows can be retried",
  retry_retryable_count_one: "1 row can be retried",
  retry_retryable_count_other: "{{count}} rows can be retried",
  retry_none_retryable: "None of these rows can be retried right now.",
  retry_reviewed_label: "I reviewed the new prices",
  retry_confirm: "Approve retry",
  retry_arming: "Read the summary…",
  retry_debit_changed: "The prices changed again. Review the updated total.",
  retry_resume_first_hint: "Resume the batch first: it still has payments pending from the earlier run.",
  retry_two_factor_required: "Enable two-factor authentication to retry payments.",
  retry_success_toast: "Retry approved. The rows are back in the queue.",
  resume_title: "Resume the run",
  resume_description:
    "Rows that were never processed go back to the queue. Rows already paid are not repeated.",
  resume_rows: "{{count}} pending rows",
  resume_rows_one: "1 pending row",
  resume_rows_other: "{{count}} pending rows",
  resume_reviewed_label: "I understand which rows will be processed",
  resume_confirm: "Resume",
  resume_arming: "Read the summary…",
  resume_two_factor_required: "Enable two-factor authentication to resume batches.",
  resume_success_toast: "Run resumed.",
  report_downloading: "Preparing the report…",
  report_error: "We could not download the report.",
};
